import {useState} from 'react'
import {MenuProvider, MenuButton, MenuButtonArrow, Menu, MenuItem} from '@ariakit/react'
import partition from 'lodash/partition'
import sortBy from 'lodash/sortBy'
import xor from 'lodash/xor'
import {exhaustive} from '@freckle/exhaustive'
import {mapMaybes, mmap, mthen, maybe} from '@freckle/maybe'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {equalExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import type {SchoolAttrs} from '@freckle/educator-entities/ts/roster/models/school'
import {Button} from '@freckle/educator-materials/ts/components/button'
import {Container} from '@freckle/educator-materials/ts/components/container'
import {H2, H3} from '@freckle/educator-materials/ts/components/typography'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import {SafeTrans} from '@freckle/educator-materials/ts/helpers/translate/component'
import RosterBody from '@freckle/school/ts/rostering/components/common/roster-body'
import type {SelectedMatchT} from '@freckle/school/ts/rostering/components/import-roster/import-roster-container'
import {
  listGroup,
  errorWrapper,
  buttonWrapper,
  selectionHeader,
  schoolSelectionItem,
  sisIdLabel,
  dropdownStyle,
  selectionText,
  noneSchoolsStyle,
  menuStyle,
  menuItemStyle,
} from './match-sisid.module.scss'

type Props = {
  schoolsMissingSisIds: Array<SchoolAttrs>
  unrecognizedSisIds: Array<string>
  onValidate: () => void
  onSelect: (unrecognizedSisIdsAddressed: boolean, matchedSelections: Array<SelectedMatchT>) => void
}

type MatchT = {
  sisId: string
  selection: Selection
}

export function MatchSisId(props: Props) {
  const {schoolsMissingSisIds, unrecognizedSisIds, onValidate, onSelect: onSelectFromProps} = props
  const {t} = useSafeTranslation()

  const [availableSchools, setAvailableSchools] = useState<SchoolAttrs[]>(schoolsMissingSisIds)

  const initEmptyMatches = unrecognizedSisIds.map(sisId => ({
    sisId,
    selection: {tag: 'no-selection-made' as const},
  }))

  const [matches, setMatches] = useState<MatchT[]>(initEmptyMatches)

  function onSelect(selection: Selection, sisId: string) {
    const allAvailableSchoolIds = schoolsMissingSisIds.map(school => school.id)
    const otherSelections = matches.filter(m => m.sisId !== sisId)
    const schoolIdsAlreadySelected = [
      ...mapMaybes(otherSelections, ({selection}) => selectionSchoolId(selection)),
      ...maybe(
        () => [],
        id => [id],
        selectionSchoolId(selection),
      ),
    ]
    const availableSchoolIds = xor(allAvailableSchoolIds, schoolIdsAlreadySelected)

    //If we're out of available schools to select from, let's set the
    //rest of the sisIds to no-match-selected
    const newMatches = (() => {
      if (availableSchoolIds.length === 0) {
        const [emptySelections, nonEmptySelections] = partition(
          otherSelections,
          match => match.selection.tag === 'no-selection-made',
        )
        const autoNoneSelections = emptySelections.map(match => ({
          sisId: match.sisId,
          selection: {tag: 'no-match-selected' as const},
        }))

        return [...nonEmptySelections, {sisId, selection}, ...autoNoneSelections]
      } else {
        return [...otherSelections, {sisId, selection}]
      }
    })()

    const newAvailableSchools = sortBy(
      schoolsMissingSisIds.filter(school => availableSchoolIds.includes(school.id)),
      s => s.name,
    )
    setMatches(newMatches)
    setAvailableSchools(newAvailableSchools)
  }

  useSafeEffectExtraDeps<{
    matches: MatchT[]
    availableSchools: SchoolAttrs[]
    onSelectFromProps: (
      unrecognizedSisIdsAddressed: boolean,
      matchedSelections: SelectedMatchT[],
    ) => void
  }>(
    ({matches, onSelectFromProps}) => {
      const emptySelections = matches.filter(match => match.selection.tag === 'no-selection-made')
      const matchedSelections = getMatchedSelections(matches)
      const unrecognizedSisIdsAddressed = emptySelections.length === 0
      onSelectFromProps(unrecognizedSisIdsAddressed, matchedSelections)
    },
    [],
    {
      matches: equalExtra(matches),
      availableSchools: equalExtra(availableSchools),
      onSelectFromProps: equalExtra(onSelectFromProps),
    },
  )

  const sortedMatches = sortBy(matches, m => m.sisId)
  const isNoneSelectedForAny = matches.find(m => m.selection.tag === 'no-match-selected')
  const emptySelections = matches.filter(m => m.selection.tag === 'no-selection-made')

  return (
    <RosterBody>
      <Container>
        <H2>{t('MATCH_SIS_ID_H2_TITLE')}</H2>

        <SafeTrans i18nKey="MATCH_SIS_ID_DESCRIPTION" />
        <br />

        <ul className={listGroup}>
          <li>
            <H3>{t('MATCH_SIS_ID_LIST_GROUP_CTA')}</H3>
          </li>
          <li className={`${schoolSelectionItem} ${selectionHeader}`}>
            <span className={sisIdLabel}>{t('MATCH_SIS_ID_LIST_GROUP_HEADING_SCHOOL_SIS_ID')}</span>
            <span>{t('MATCH_SIS_ID_LIST_GROUP_HEADING_SCHOOL_NAME')}</span>
          </li>
          {sortedMatches.map((match, i: number) => (
            <SchoolSelection
              key={i}
              match={match}
              availableSchools={availableSchools}
              onSelect={onSelect}
            />
          ))}
        </ul>
        <br />
        <div className={isNoneSelectedForAny ? errorWrapper : buttonWrapper}>
          {isNoneSelectedForAny ? (
            <span className={noneSchoolsStyle}>{t('MATCH_SIS_ID_WARNING_NONE_SELECTED')}</span>
          ) : null}
          <Button
            dataTest="rostering-match-sis-id-continue"
            onClick={onValidate}
            disabled={emptySelections.length > 0}
          >
            {t('MATCH_SIS_ID_BUTTON_VALIDATE')}
          </Button>
        </div>
      </Container>
    </RosterBody>
  )
}

type Selection =
  // SIS ID was matched to a school
  | {tag: 'school-selected'; selection: SchoolAttrs}
  // SIS ID was explicitly declared as not matching
  | {tag: 'no-match-selected'}
  // A selection has not yet been made for SIS ID
  | {tag: 'no-selection-made'}

function matchSelection<T>(
  sel: Selection,
  {
    onSchool,
    onNoSelection,
    onNoMatch,
  }: {onSchool: (school: SchoolAttrs) => T; onNoSelection: () => T; onNoMatch: () => T},
): T {
  switch (sel.tag) {
    case 'school-selected':
      return onSchool(sel.selection)
    case 'no-selection-made':
      return onNoSelection()
    case 'no-match-selected':
      return onNoMatch()
    default:
      return exhaustive(sel)
  }
}

const selectionSchoolId = (selection: Selection): number | null =>
  matchSelection(selection, {
    onSchool: school => school.id,
    onNoSelection: () => null,
    onNoMatch: () => null,
  })

const selectionSchool = (selection: Selection): SchoolAttrs | null =>
  matchSelection(selection, {
    onSchool: school => school,
    onNoSelection: () => null,
    onNoMatch: () => null,
  })

type SchoolSelectionProps = {
  match: MatchT
  availableSchools: Array<SchoolAttrs>
  onSelect: (selection: Selection, sisId: string) => void
}

function SchoolSelection(props: SchoolSelectionProps) {
  const {
    match: {sisId, selection},
    availableSchools,
    onSelect,
  } = props
  const {t} = useSafeTranslation()

  const title = matchSelection(selection, {
    onSchool: school => <SelectionText school={school} />,
    onNoSelection: () => <>{t('SCHOOL_SELECTION_LABEL')}</>,
    onNoMatch: () => (
      <span className={noneSchoolsStyle}>{t('SCHOOL_SELECTION_LABEL_NONE_SELETED')}</span>
    ),
  })

  const menuButtonId = `sis-id-matching-select-school-dropdown-${sisId}`

  return (
    <li className={schoolSelectionItem}>
      <span className={sisIdLabel}>{sisId}</span>

      <MenuProvider>
        <MenuButton className={dropdownStyle} id={menuButtonId} data-test={menuButtonId}>
          {title}
          <MenuButtonArrow />
        </MenuButton>
        <Menu className={menuStyle} sameWidth>
          {availableSchools.map(school => (
            <MenuItem
              key={school.id}
              onClick={() => onSelect({tag: 'school-selected', selection: school}, sisId)}
              data-test={`sis-id-matching-${school.id}`}
              className={menuItemStyle}
            >
              <SelectionText school={school} />
            </MenuItem>
          ))}
          <MenuItem
            key="NO_MATCH_SELECTED"
            onClick={() => onSelect({tag: 'no-match-selected'}, sisId)}
            className={menuItemStyle}
          >
            <span className={noneSchoolsStyle}>{t('SCHOOL_SELECTION_LABEL_NONE_SELETED')}</span>
          </MenuItem>
        </Menu>
      </MenuProvider>
    </li>
  )
}

const SelectionText = ({school}: {school: SchoolAttrs}) => (
  <span className={selectionText}>
    {school.name}. {mmap(a => a.join('\n'), school.formattedAddressLines)}
  </span>
)

const getMatchedSelections = (matches: Array<MatchT>): Array<SelectedMatchT> =>
  mapMaybes(matches, match =>
    mthen(selectionSchool(match.selection), school => ({
      sisId: match.sisId,
      school,
    })),
  )
